import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { api } from '../../lib/api';

// ── Icons ─────────────────────────────────────────────────────────────────────
const sz = { width: 20, height: 20, display: 'block' } as const;

function IconUser()     { return <svg style={sz} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><circle cx="12" cy="8" r="4"/><path strokeLinecap="round" d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>; }
function IconBuilding() { return <svg style={sz} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M3 7l9-4 9 4M4 7v14M20 7v14M9 21V11h6v10"/></svg>; }
function IconShield()   { return <svg style={sz} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>; }
function IconBox()      { return <svg style={sz} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function IconWrench()   { return <svg style={sz} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>; }
function IconClipboard(){ return <svg style={sz} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>; }

type Stats = {
  users: number; companies: number; registrations: number;
  activeRegs: number; claims: number; openClaims: number; regsThisMonth: number;
};
type RegRow   = { id: number; serialNumber: string; status: string; createdAt: string; user: { email: string } | null; product: { name: string } | null };
type ClaimRow = { id: number; subject: string; status: string; priority: string; createdAt: string; user: { email: string; firstName: string | null } | null };

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  open:      { bg: '#dbeafe', color: '#1d4ed8' },
  in_review: { bg: '#fef9c3', color: '#a16207' },
  approved:  { bg: '#dcfce7', color: '#15803d' },
  rejected:  { bg: '#fee2e2', color: '#b91c1c' },
  resolved:  { bg: '#f3f4f6', color: '#6b7280' },
  active:    { bg: '#dcfce7', color: '#15803d' },
  voided:    { bg: '#f3f4f6', color: '#6b7280' },
  expired:   { bg: '#fee2e2', color: '#b91c1c' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_COLORS[status] ?? { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      {status.replace('_', ' ')}
    </span>
  );
}

function BCTestButton() {
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  return (
    <div className="flex items-center gap-3">
      <button style={{ fontSize: 12, padding: '6px 12px', border: '1px solid #e5e5e5', borderRadius: 6, background: '#fff', cursor: 'pointer', color: '#374151' }}
        disabled={loading}
        onClick={async () => { setLoading(true); try { const r = await api.get<any>('/admin/bc/test'); setResult(r.ok ? `✓ ${r.message}` : `✗ ${r.message}`); } catch { setResult('✗ Failed'); } setLoading(false); }}>
        {loading ? 'Testing…' : 'Test BC'}
      </button>
      {result && <span style={{ fontSize: 12, color: result.startsWith('✓') ? '#16a34a' : '#dc2626' }}>{result}</span>}
    </div>
  );
}

export default function AdminOverview() {
  const [stats, setStats]   = useState<Stats | null>(null);
  const [regs, setRegs]     = useState<RegRow[]>([]);
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<Stats>('/admin/stats').catch(() => null),
      api.get<RegRow[]>('/admin/registrations?limit=5').catch(() => []),
      api.get<ClaimRow[]>('/admin/claims?status=open&limit=5').catch(() => []),
    ]).then(([s, r, c]) => { setStats(s); setRegs(r as RegRow[]); setClaims(c as ClaimRow[]); setLoading(false); });
  }, []);

  const statCards = stats ? [
    { label: 'Total users',          value: stats.users,         Icon: IconUser,      to: '/admin/users',          color: '#6366f1' },
    { label: 'Companies',            value: stats.companies,     Icon: IconBuilding,  to: '/admin/companies',      color: '#0284c7' },
    { label: 'Active registrations', value: stats.activeRegs,    Icon: IconShield,    to: '/admin/registrations',  color: '#16a34a' },
    { label: 'Regs this month',      value: stats.regsThisMonth, Icon: IconBox,       to: '/admin/registrations',  color: '#d97706' },
    { label: 'Open claims',          value: stats.openClaims,    Icon: IconWrench,    to: '/admin/claims',         color: '#dc2626' },
    { label: 'Total claims',         value: stats.claims,        Icon: IconClipboard, to: '/admin/claims',         color: '#374151' },
  ] : [];

  return (
    <Layout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold" style={{ color: '#0b0d0e' }}>Admin Overview</h1>
        <BCTestButton />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
        {loading ? Array(6).fill(0).map((_, i) => (
          <div key={i} className="rounded-xl p-4" style={{ background: '#fff', border: '1px solid #e5e5e5' }}>
            <div style={{ height: 32, width: 60, background: '#f3f4f6', borderRadius: 6 }} />
          </div>
        )) : statCards.map(s => (
          <Link key={s.label} to={s.to} style={{ textDecoration: 'none' }}>
            <div className="rounded-xl p-4 transition-shadow hover:shadow-md" style={{ background: '#fff', border: '1px solid #e5e5e5', height: '100%' }}>
              <div style={{ color: s.color, marginBottom: 8 }}><s.Icon /></div>
              <p style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value.toLocaleString()}</p>
              <p style={{ fontSize: 12, color: '#8f9b9b', marginTop: 4 }}>{s.label}</p>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent registrations */}
        <div className="rounded-xl" style={{ background: '#fff', border: '1px solid #e5e5e5', overflow: 'hidden' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#f0f0f0' }}>
            <p className="font-semibold text-sm" style={{ color: '#0b0d0e' }}>Recent registrations</p>
            <Link to="/admin/registrations" style={{ fontSize: 12, color: '#e00000', textDecoration: 'none' }}>View all →</Link>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 380 }}>
              <tbody>
                {regs.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #fafafa' }}>
                    <td style={{ padding: '10px 16px' }}>
                      <p style={{ fontWeight: 500, color: '#0b0d0e' }}>{r.product?.name ?? '—'}</p>
                      <p style={{ fontSize: 11, color: '#8f9b9b', fontFamily: 'monospace' }}>{r.serialNumber}</p>
                    </td>
                    <td style={{ padding: '10px 16px', color: '#8f9b9b', fontSize: 12 }}>{r.user?.email}</td>
                    <td style={{ padding: '10px 16px' }}><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
                {!loading && regs.length === 0 && <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: '#8f9b9b', fontSize: 13 }}>No registrations yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Open claims */}
        <div className="rounded-xl" style={{ background: '#fff', border: '1px solid #e5e5e5', overflow: 'hidden' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#f0f0f0' }}>
            <p className="font-semibold text-sm" style={{ color: '#0b0d0e' }}>Open claims</p>
            <Link to="/admin/claims" style={{ fontSize: 12, color: '#e00000', textDecoration: 'none' }}>View all →</Link>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', minWidth: 340 }}>
              <tbody>
                {claims.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #fafafa' }}>
                    <td style={{ padding: '10px 16px' }}>
                      <p style={{ fontWeight: 500, color: '#0b0d0e' }}>{c.subject}</p>
                      <p style={{ fontSize: 11, color: '#8f9b9b' }}>{c.user?.email}</p>
                    </td>
                    <td style={{ padding: '10px 16px' }}><StatusBadge status={c.priority} /></td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#8f9b9b' }}>{c.createdAt.slice(0, 10)}</td>
                  </tr>
                ))}
                {!loading && claims.length === 0 && <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: '#8f9b9b', fontSize: 13 }}>No open claims.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quick nav */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
        {[
          { to: '/admin/claims',        label: 'Manage claims',  Icon: IconWrench   },
          { to: '/admin/registrations', label: 'Registrations',  Icon: IconShield   },
          { to: '/admin/companies',     label: 'Companies',      Icon: IconBuilding },
          { to: '/admin/users',         label: 'User accounts',  Icon: IconUser     },
        ].map(item => (
          <Link key={item.to} to={item.to}
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, background: '#fff', border: '1px solid #e5e5e5', color: '#374151', fontSize: 13, fontWeight: 500 }}>
            <span style={{ color: '#8f9b9b' }}><item.Icon /></span>
            {item.label}
          </Link>
        ))}
      </div>
    </Layout>
  );
}
